import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { markAlertsAsViewed } from '@/store/slices/alertsSlice';
import { useMsalAuth } from '@/hooks/useMsalAuth';
import { AgentStatusDropdown } from '@/components/amazon-connect/AgentStatusDropdown';
import { ROUTES } from '@/constants';
import { Paper, Box, Typography, Button, Avatar, Badge } from '@gapartners/ui';
import { Notifications as NotificationsIcon } from '@mui/icons-material';

export function Header() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { hasUnviewedAlerts } = useAppSelector(state => state.alerts);
  const { logout } = useMsalAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
  };

  const handleTeamClick = () => {
    navigate(ROUTES.TEAM);
  };

  const handleLogoClick = () => {
    navigate(ROUTES.DASHBOARD);
  };

  const handleManagerClick = () => {
    navigate(`${ROUTES.MANAGER.ROOT}/${ROUTES.MANAGER.TEAM}`);
  };

  const handleAlertsClick = () => {
    dispatch(markAlertsAsViewed());
    navigate(`${ROUTES.MANAGER.ROOT}/${ROUTES.MANAGER.ALERTS}`);
  };

  return (
    <Paper
      elevation={0}
      square
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 64,
        px: 3,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="text.primary"
            onClick={handleLogoClick}
            sx={{
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
              transition: 'color 0.2s',
            }}
          >
            {t('common.appName')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {user && (
            <Box
              component={NavLink}
              to={ROUTES.PROFILE}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {user.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.2s',
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {user.name?.charAt(0).toUpperCase() ||
                  user.email?.charAt(0).toUpperCase()}
              </Avatar>
            </Box>
          )}
          <AgentStatusDropdown />
          <Button variant="outlined" color="inherit" onClick={handleTeamClick}>
            {t('auth.team')}
          </Button>
          {user?.isManager && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleManagerClick}
            >
              {t('auth.manager')}
            </Button>
          )}
          {user?.isManager && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleAlertsClick}
              sx={{
                position: 'relative',
                animation: hasUnviewedAlerts
                  ? 'sparkle 1.5s ease-in-out infinite'
                  : 'none',
                '@keyframes sparkle': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                },
              }}
            >
              <Badge
                badgeContent={hasUnviewedAlerts ? 1 : 0}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#DC2626',
                    borderRadius: '50%',
                    width: 8,
                    height: 8,
                    minWidth: 8,
                    padding: 0,
                  },
                }}
              >
                <NotificationsIcon sx={{ mr: 0.5 }} />
                {t('auth.alerts')}
              </Badge>
            </Button>
          )}
          <Button variant="outlined" color="inherit" onClick={handleLogout}>
            {t('auth.logout')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
